/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 26/05/2025 - 19:25:16
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 26/05/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect } from "react";
import images from "../../images/index";
import Navbar from "../../components/shared/navbar";
import Footer from "../../components/shared/footer";
import { Link, useNavigate } from "react-router-dom";
import { Image, Container, Row, Col, Form } from "react-bootstrap";
import { auth } from "../../firebase";
import { toast } from "react-toastify";
import Loader from "../../components/shared/loader";
import { api } from "../../api/transaction";
import { useTranslation } from 'react-i18next';

const AnalysisService: React.FC = () => {
  const { t } = useTranslation(['analysisService', 'common']);
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(false);
  const user = auth.currentUser;
  const displayName = user?.displayName;
  const email = user?.email;

  const [firstName, ...lastNameArr] = displayName?.split(" ") || [];
  const lastName = lastNameArr.join(" ");

  const [orderData, setOrderData] = useState({
    firstName: firstName || "",
    lastName: lastName || "",
    email: email || "",
    phoneNumber: "",
  });

  const [termsChecked, setTermsChecked] = useState(false);
  const [termsError, setTermsError] = useState("");

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOrderData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name as keyof typeof errors]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTermsChecked(e.target.checked);
    if (e.target.checked) {
      setTermsError("");
    } else {
      setTermsError(t('analysisService:analysisService.customerInfo.errors.termsError'));
    }
  };

  // Function to validate phone number
  const validatePhoneNumber = () => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (orderData.phoneNumber.trim().length === 0) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: t('analysisService:analysisService.customerInfo.errors.phoneRequired')
      }));
      return false;
    } else if (!phoneRegex.test(orderData.phoneNumber.replace(/\s/g, ""))) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: t('analysisService:analysisService.customerInfo.errors.phoneInvalid')
      }));
      return false;
    }
    return true;
  };

  // Add focus/blur handlers for real-time validation
  const handlePhoneBlur = () => {
    validatePhoneNumber();
  };

  const handleTermsBlur = () => {
    if (!termsChecked) {
      setTermsError(t('analysisService:analysisService.customerInfo.errors.termsError'));
    } else {
      setTermsError("");
    }
  };

  const formValidation = () => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    let formValid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      phoneNumber: "",
    };

    if (orderData.firstName.trim().length === 0) {
      formValid = false;
      newErrors.firstName = t('analysisService:analysisService.customerInfo.errors.firstName');
    }
    if (orderData.lastName.trim().length === 0) {
      formValid = false;
      newErrors.lastName = t('analysisService:analysisService.customerInfo.errors.lastName');
    }
    if (orderData.phoneNumber.trim().length === 0) {
      formValid = false;
      newErrors.phoneNumber = t('analysisService:analysisService.customerInfo.errors.phoneRequired');
    } else if (!phoneRegex.test(orderData.phoneNumber.replace(/\s/g, ""))) {
      formValid = false;
      newErrors.phoneNumber = t('analysisService:analysisService.customerInfo.errors.phoneInvalid');
    }

    setErrors(newErrors);

    if (!termsChecked) {
      setTermsError(t('analysisService:analysisService.customerInfo.errors.termsError'));
      formValid = false;
    }

    return formValid;
  };

  const proceedToPayment = async () => {
    if (!formValidation()) {
      toast.error(t('analysisService:analysisService.customerInfo.errors.formError'));
      return;
    }

    setLoader(true);

    try {
      const orderPayload = {
        userId: user?.uid || "",
        email: orderData.email,
        userName: `${orderData.firstName} ${orderData.lastName}`,
        projectData: {
          projectId: "temp_project_id",
          projectName: "Analysis Service Request",
          location: "",
        },
        contactInfo: {
          firstName: orderData.firstName,
          lastName: orderData.lastName,
          phoneNumber: orderData.phoneNumber,
        }
      };

      const response = await api.createAnalysisOrder(orderPayload);

      if (response.data.success) {
        toast.success(t('analysisService:analysisService.notifications.success'));
        
        navigate('/analysispayment', { 
          state: { 
            clientSecret: response.data.data.clientSecret,
            orderId: response.data.data.orderId,
            paymentIntentId: response.data.data.paymentIntentId,
            amount: response.data.data.amount,
            service: "analysis",
            analysisOrderId: response.data.data.orderId,
            userId: user?.uid,
            email: orderData.email,
            userName: `${orderData.firstName} ${orderData.lastName}`
          } 
        });
      } else {
        throw new Error(response.data.message || t('analysisService:analysisService.notifications.error'));
      }
    } catch (error: any) {
      console.error("Error creating analysis order:", error);
      
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          t('analysisService:analysisService.notifications.error');
      
      toast.error(errorMessage);
    } finally {
      setLoader(false);
    }
  };

  if (!user) {
    return null;
  }

  // Check if all required fields are filled
  const isContactInfoComplete = 
    orderData.firstName.trim() !== "" && 
    orderData.lastName.trim() !== "" && 
    orderData.phoneNumber.trim() !== "" &&
    /^[\+]?[1-9][\d]{0,15}$/.test(orderData.phoneNumber.replace(/\s/g, ""));

  return (
    <>
    <Navbar />
    
    {/* Header Section */}
    <div style={{ background: "#4274a5", color: "white", padding: "60px 0" }}>
      <Container>
        <Row className="align-items-center">
          <Col md={8}>
            <div className="d-flex align-items-center mb-3">
              <Link to="/dashboard" className="text-white text-decoration-none me-3">
                <Image src={images.leftArrow} width={24} height={24} alt="back" className="me-2" />
                <span>{t('analysisService:analysisService.header.backToDashboard')}</span>
              </Link>
            </div>
            <h1 className="display-4 fw-bold mb-3">{t('analysisService:analysisService.header.title')}</h1>
            <p className="lead mb-4">{t('analysisService:analysisService.header.subtitle')}</p>
            <div className="d-flex align-items-center">
              <span className="badge bg-light text-dark p-2 me-3">{t('analysisService:analysisService.header.badges.professional')}</span>
              <span className="badge bg-light text-dark p-2">{t('analysisService:analysisService.header.badges.reports')}</span>
            </div>
          </Col>
          <Col md={4} className="text-center">
            <div style={{ 
              background: "rgba(255,255,255,0.1)", 
              backdropFilter: "blur(10px)",
              borderRadius: "20px",
              padding: "30px",
              border: "1px solid rgba(255,255,255,0.2)"
            }}>
              <h2 className="display-3 fw-bold mb-2">{t('analysisService:analysisService.header.price')}</h2>
              <p className="mb-0">{t('analysisService:analysisService.header.paymentType')}</p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>

    <Container className="py-5">
      <Row>
        <Col lg={8}>
          <div className="mb-5">
            <h3 className="fw-bold mb-4">{t('analysisService:analysisService.included.title')}</h3>
            <Row>
              <Col md={6}>
                <div className="d-flex mb-3">
                  <div className="flex-shrink-0 me-3">
                    <div style={{ 
                      width: "40px", 
                      height: "40px", 
                      background: "#e3f2fd", 
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      📊
                    </div>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">{t('analysisService:analysisService.included.items.report.title')}</h6>
                    <p className="text-muted mb-0 small">{t('analysisService:analysisService.included.items.report.description')}</p>
                  </div>
                </div>
                
                <div className="d-flex mb-3">
                  <div className="flex-shrink-0 me-3">
                    <div style={{ 
                      width: "40px", 
                      height: "40px", 
                      background: "#e8f5e8", 
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      💡
                    </div>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">{t('analysisService:analysisService.included.items.recommendations.title')}</h6>
                    <p className="text-muted mb-0 small">{t('analysisService:analysisService.included.items.recommendations.description')}</p>
                  </div>
                </div>
                
                <div className="d-flex mb-3">
                  <div className="flex-shrink-0 me-3">
                    <div style={{ 
                      width: "40px", 
                      height: "40px", 
                      background: "#FFF8E1", /* Light amber background */
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      ⚙️
                    </div>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">{t('analysisService:analysisService.included.items.parameters.title')}</h6>
                    <p className="text-muted mb-0 small">{t('analysisService:analysisService.included.items.parameters.description')}</p>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <div className="mb-5 p-4" style={{ background: "#f8f9fa", borderRadius: "12px", border: "1px solid #e9ecef" }}>
            <h4 className="fw-bold mb-3">{t('analysisService:analysisService.requirements.title')}</h4>
            <p>
              <strong>{t('analysisService:analysisService.requirements.description')}</strong>
            </p>
            <p>
              <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" className="fw-semibold text-decoration-none">
                <i className="fas fa-map-marker-alt me-2"></i>{t('analysisService:analysisService.requirements.googleMaps')}
              </a>
            </p>
            <div className="mt-3 mb-3">
              <p className="mb-2 fw-semibold">{t('analysisService:analysisService.requirements.note')}</p>
              <ul>
                {Array.isArray(t('analysisService:analysisService.requirements.items', { returnObjects: true })) 
                  ? (t('analysisService:analysisService.requirements.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))
                  : null
                }
              </ul>
            </div>
            
            <div className="mt-3">
              <Form.Check 
                type="checkbox" 
                id="terms-checkbox"
                className={termsError ? "is-invalid" : ""}
                checked={termsChecked}
                onChange={handleCheckboxChange}
                onBlur={handleTermsBlur}
                label={<span className="fw-semibold">{t('analysisService:analysisService.requirements.confirmation')}</span>}
              />
              {termsError && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {termsError}
                </div>
              )}
            </div>
          </div>
          <div style={{ 
            background: "#fff", 
            borderRadius: "16px", 
            padding: "40px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            border: "1px solid #f0f0f0"
          }}>
            <h3 className="fw-bold mb-4">{t('analysisService:analysisService.customerInfo.title')}</h3>
            
            <Row>
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">{t('analysisService:analysisService.customerInfo.fields.firstName')}</label>
                <input
                  type="text"
                  className={`form-control form-control-lg ${errors.firstName ? 'is-invalid' : ''}`}
                  name="firstName"
                  value={orderData.firstName}
                  onChange={handleChange}
                  style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                />
                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
              </Col>
              
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">{t('analysisService:analysisService.customerInfo.fields.lastName')}</label>
                <input
                  type="text"
                  className={`form-control form-control-lg ${errors.lastName ? 'is-invalid' : ''}`}
                  name="lastName"
                  value={orderData.lastName}
                  onChange={handleChange}
                  style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                />
                {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
              </Col>
            </Row>
            
            <Row>
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">{t('analysisService:analysisService.customerInfo.fields.email')}</label>
                <input
                  type="email"
                  className="form-control form-control-lg"
                  value={orderData.email}
                  disabled
                  style={{ 
                    borderRadius: "10px", 
                    border: "2px solid #e9ecef",
                    backgroundColor: "#f8f9fa",
                    cursor: "not-allowed"
                  }}
                />
                <small className="text-muted">{t('analysisService:analysisService.customerInfo.fields.emailNote')}</small>
              </Col>
              
              <Col md={6} className="mb-3">
                <label className="form-label fw-semibold">{t('analysisService:analysisService.customerInfo.fields.phoneNumber')}</label>
                <input
                  type="tel"
                  className={`form-control form-control-lg ${errors.phoneNumber ? 'is-invalid' : ''}`}
                  name="phoneNumber"
                  value={orderData.phoneNumber}
                  onChange={handleChange}
                  onBlur={handlePhoneBlur}
                  placeholder={t('analysisService:analysisService.customerInfo.fields.phonePlaceholder')}
                  style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                />
                {errors.phoneNumber && (
                  <div className="invalid-feedback" style={{ display: 'block' }}>
                    {errors.phoneNumber}
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </Col>

        {/* Right Column - Order Summary */}
        <Col lg={4}>
          <div style={{ 
            position: "sticky", 
            top: "100px",
            background: "#fff", 
            borderRadius: "16px", 
            padding: "30px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            border: "1px solid #f0f0f0"
          }}>
            <h4 className="fw-bold mb-4">{t('analysisService:analysisService.orderSummary.title')}</h4>
            
            <div className="d-flex justify-content-between align-items-center mb-3 pb-3" style={{ borderBottom: "1px solid #f0f0f0" }}>
              <div>
                <h6 className="mb-1">{t('analysisService:analysisService.orderSummary.service.title')}</h6>
                <small className="text-muted">{t('analysisService:analysisService.orderSummary.service.description')}</small>
              </div>
              <span className="fw-bold">{t('analysisService:analysisService.header.price')}</span>
            </div>
            
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="fw-bold mb-0">{t('analysisService:analysisService.orderSummary.total')}</h5>
              <h4 className="fw-bold text-primary mb-0">{t('analysisService:analysisService.header.price')}</h4>
            </div>
            
            {/* Requirements Checklist */}
            <div className="mb-3 p-3" style={{ 
              background: "#f8f9fa", 
              borderRadius: "8px", 
              border: "1px solid #e9ecef" 
            }}>
              <p className="fw-semibold mb-2 small">
                {t('analysisService:analysisService.orderSummary.requirementsCheck.title', 'Complete these steps to proceed:')}
              </p>
              
              {/* Terms and conditions check */}
              <div className={`d-flex align-items-center mb-2 ${termsChecked ? 'text-success' : 'text-danger'}`}>
                <i className={`me-2 fas ${termsChecked ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                <small>
                  {termsChecked 
                    ? t('analysisService:analysisService.orderSummary.requirementsCheck.termsComplete', 'Terms and conditions accepted') 
                    : t('analysisService:analysisService.orderSummary.requirementsCheck.termsIncomplete', 'Accept terms and conditions')}
                </small>
              </div>
              
              {/* Field-specific checks - only show the ones that are missing */}
              {!orderData.firstName.trim() && (
                <div className="d-flex align-items-center mb-2 text-danger">
                  <i className="me-2 fas fa-exclamation-circle"></i>
                  <small>{t('analysisService:analysisService.orderSummary.requirementsCheck.fieldChecks.firstNameMissing', 'First Name is required')}</small>
                </div>
              )}
              
              {!orderData.lastName.trim() && (
                <div className="d-flex align-items-center mb-2 text-danger">
                  <i className="me-2 fas fa-exclamation-circle"></i>
                  <small>{t('analysisService:analysisService.orderSummary.requirementsCheck.fieldChecks.lastNameMissing', 'Last Name is required')}</small>
                </div>
              )}
              
              {!orderData.phoneNumber.trim() && (
                <div className="d-flex align-items-center mb-2 text-danger">
                  <i className="me-2 fas fa-exclamation-circle"></i>
                  <small>{t('analysisService:analysisService.orderSummary.requirementsCheck.fieldChecks.phoneNumberMissing', 'Phone Number is required')}</small>
                </div>
              )}
              
              {orderData.phoneNumber.trim() && !/^[\+]?[1-9][\d]{0,15}$/.test(orderData.phoneNumber.replace(/\s/g, "")) && (
                <div className="d-flex align-items-center mb-2 text-danger">
                  <i className="me-2 fas fa-exclamation-circle"></i>
                  <small>{t('analysisService:analysisService.orderSummary.requirementsCheck.fieldChecks.phoneNumberInvalid', 'Please enter a valid phone number')}</small>
                </div>
              )}
              
              {/* Only show completion message if everything is complete */}
              {termsChecked && isContactInfoComplete && (
                <div className="d-flex align-items-center text-success">
                  <i className="me-2 fas fa-check-circle"></i>
                  <small>{t('analysisService:analysisService.orderSummary.requirementsCheck.contactInfoComplete', 'All required information complete')}</small>
                </div>
              )}
            </div>
            
            <button 
              onClick={proceedToPayment} 
              disabled={loader}
              className="btn btn-primary btn-lg w-100"
              style={{ 
                borderRadius: "12px",
                background: "linear-gradient(135deg, #667eea 0%, #4274a5 100%)",
                border: "none",
                padding: "15px",
                fontSize: "16px",
                fontWeight: "600",
                transition: "all 0.3s ease"
              }}
            >
              {loader ? (
                <span className="d-flex justify-content-center align-items-center">
                  <Loader />
                  <span className="ms-2">{t('analysisService:analysisService.orderSummary.processing')}</span>
                </span>
              ) : (
                t('analysisService:analysisService.orderSummary.button')
              )}
            </button>
            
            <div className="text-center mt-3">
              <small className="text-muted">
                {t('analysisService:analysisService.orderSummary.security')}
              </small>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-4 pt-3" style={{ borderTop: "1px solid #f0f0f0" }}>
              <div className="row text-center">
                <div className="col-4">
                  <div className="text-primary mb-1">⚡</div>
                  <small className="text-muted">{t('analysisService:analysisService.orderSummary.features.delivery')}</small>
                </div>
                <div className="col-4">
                  <div className="text-primary mb-1">🎯</div>
                  <small className="text-muted">{t('analysisService:analysisService.orderSummary.features.analysis')}</small>
                </div>
                <div className="col-4">
                  <div className="text-primary mb-1">📞</div>
                  <small className="text-muted">{t('analysisService:analysisService.orderSummary.features.support')}</small>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
    
    <Footer />
  </>
  );
};

export default AnalysisService;